import type { components } from '../../lib/api/schema.gen'
import { derivePoGroups } from '../cart/po-groups'
import type { PoGroupDraft } from '../cart/po-groups'

/**
 * Re-exported from `@misumi/shared` (UID-98 PR-A): `formatOrderRef` was promoted out of
 * this vertical into the shared domain so the orders list/detail (S5/S6) and the
 * post-checkout animation all format the SO number the same way. This barrel re-export
 * keeps existing call sites (`OrderPlacedCelebration`, this module's test) importing from
 * `./checkout-domain` unchanged — a behaviour-preserving move.
 */
export { formatOrderRef } from '@misumi/shared'

export type ShipToAddress = components['schemas']['ShipToAddress']
export type ShippingMethodOption = components['schemas']['ShippingMethodOption']
export type SavedPaymentMethod = components['schemas']['SavedPaymentMethod']
export type CheckoutView = components['schemas']['CheckoutView']
export type CheckoutRequest = components['schemas']['CheckoutRequest']
export type PaymentSelection = components['schemas']['PaymentSelection']
export type PaymentMethodKind = components['schemas']['PaymentMethodKind']
export type QuoteResult = components['schemas']['QuoteResult']
export type LineItem = components['schemas']['LineItem']
export type CartLinePresentation = components['schemas']['CartLinePresentation']
export type ShipMode = components['schemas']['ShipMode']
export type ShipPlan = components['schemas']['ShipPlan']

// ---------------------------------------------------------------------------
// UID-97 K1 — split-PO / multi-PO placement (ADR-0044 §1)
// ---------------------------------------------------------------------------

export type OrderGroup = components['schemas']['OrderGroup']
export type PlaceOrdersRequest = components['schemas']['PlaceOrdersRequest']
export type PlaceOrdersResult = components['schemas']['PlaceOrdersResult']
export type PoBreakdownGroup = components['schemas']['PoBreakdownGroup']

// `derivePoGroups` + `PoGroupDraft` moved into the cart vertical (`../cart/po-groups`)
// for UID-315 (ADR-0106 decision F): PO grouping IS readiness-bucket grouping, a
// cart-owned concept. Re-exported here so this vertical's existing call sites
// (`CheckoutPage`, `PaymentStep`, and `buildOrderGroups` below) keep importing from
// `./checkout-domain` unchanged — a behaviour-preserving move.
export { derivePoGroups }
export type { PoGroupDraft }

/** Builds the `PlaceOrdersRequest.groups[]` from the drafted groups + the
 * buyer's per-group PO-number inputs (blank/whitespace-only → `null`, "no
 * PO", matching `OrderGroup.poReference`'s nullable contract). */
export function buildOrderGroups(
  groups: readonly PoGroupDraft[],
  poNumbers: Readonly<Record<string, string>>,
): OrderGroup[] {
  return groups.map((group) => {
    const raw = poNumbers[group.key]?.trim()
    return { poReference: raw || null, lineIds: [...group.lineIds] }
  })
}

/**
 * Gate 5 C1 fix — split + `po_invoice`: every group's PO-number input is
 * REQUIRED, not merely optional, for this payment kind specifically.
 * `OrderGroup.poReference` is nullable on the wire (a PO-less group is a
 * valid *shape*), but the frozen `PaymentSelection` `po_invoice` variant
 * requires a non-empty top-level `poNumber` (ajv `minLength: 1`) — so
 * paying by PO with a blank group would either fail that ajv check (if no
 * group supplies a fallback) or silently borrow a DIFFERENT group's PO
 * number as the shared fallback (misleading — see
 * `firstNonBlankPoReference` below). Blocking here, per-group, keeps every
 * group's PO honest and visible instead of one silently substituting for
 * another's.
 */
export function validatePoGroupNumbers(
  groups: readonly PoGroupDraft[],
  poNumbers: Readonly<Record<string, string>>,
): FieldErrors {
  const errors: FieldErrors = {}
  for (const group of groups) {
    if (!poNumbers[group.key]?.trim()) {
      errors[group.key] = 'PO number is required.'
    }
  }
  return errors
}

/**
 * Gate 5 C1 fix — the shared top-level `PaymentSelection` (`po_invoice`
 * variant) requires a non-empty `poNumber` even in split mode, where the
 * buyer only ever sees/fills the PER-GROUP inputs (the single PO field is
 * hidden, `PaymentStep.tsx`). Without this, `payment.poNumber` stays `''`
 * and the BFF's ajv validation rejects `POST /orders/batch` with a 400
 * before the handler runs — split + PO-invoice was completely broken
 * (Gate 5 C1). The BFF overrides each ORDER's own PO with that order's
 * group `poReference` when present (ADR-0044 §1) — the shared
 * `payment.poNumber` is only the fallback/contract-satisfying value, never
 * the per-order source of truth — so populating it from the first
 * non-blank group PO is safe: it never overrides a group's own PO, it only
 * keeps the request valid. Callers MUST run {@link validatePoGroupNumbers}
 * first so every group actually has one (a `find` that returns `undefined`
 * here would silently re-introduce the empty-string bug).
 */
export function firstNonBlankPoReference(
  groups: readonly PoGroupDraft[],
  poNumbers: Readonly<Record<string, string>>,
): string | undefined {
  for (const group of groups) {
    const trimmed = poNumbers[group.key]?.trim()
    if (trimmed) return trimmed
  }
  return undefined
}

/** "Place order" / "Place N orders" CTA label (prototype `placeLabel`,
 * `Fictiv Platform.dc.html` line 7433: `splitPO?('Place '+nGroups+' orders'):'Place order'`). */
export function placeOrderCtaLabel(groupCount: number): string {
  return groupCount > 1 ? `Place ${groupCount} orders` : 'Place order'
}

/** Summary-rail "Placing N purchase orders" note (prototype `co.splitNote`,
 * line 2132: `nGroups+' purchase orders'`, rendered as "Placing {{ co.splitNote }}"). */
export function placingPurchaseOrdersNote(groupCount: number): string {
  return `Placing ${groupCount} purchase order${groupCount === 1 ? '' : 's'}`
}

// ---------------------------------------------------------------------------
// UID-97 K2 — saved ship-to address CRUD (ADR-0044 §2)
// ---------------------------------------------------------------------------

export type AddressView = components['schemas']['AddressView']
export type CreateAddressRequest = components['schemas']['CreateAddressRequest']
export type UpdateAddressRequest = components['schemas']['UpdateAddressRequest']
export type AddressMutationResult = components['schemas']['AddressMutationResult']

export interface AddressFormFields {
  readonly label: string
  readonly contactName: string
  readonly companyName: string
  readonly line1: string
  readonly line2: string
  readonly city: string
  readonly region: string
  readonly postalCode: string
  readonly countryCode: string
}

export const BLANK_ADDRESS_FORM: AddressFormFields = {
  label: '',
  contactName: '',
  companyName: '',
  line1: '',
  line2: '',
  city: '',
  region: '',
  postalCode: '',
  countryCode: 'US',
}

/** Full name + company are OPTIONAL on the wire (K4 is a fixture-seeded
 * stand-in, ADR-0044 §3) but the Add/Edit modal itself (prototype lines
 * 3791-3804) does not mark them required either — only the address value
 * fields are. */
export function validateAddressFields(fields: AddressFormFields): FieldErrors {
  const errors: FieldErrors = {}
  if (!fields.label.trim()) errors.label = 'Enter a label for this address.'
  if (!fields.line1.trim()) errors.line1 = 'Enter a street address.'
  if (!fields.city.trim()) errors.city = 'Enter a city.'
  if (!fields.region.trim()) errors.region = 'Enter a state/region.'
  // A real ISO 3166-1 alpha-2 code — not just "non-blank" (which let "U" or
  // "1" through despite the input's maxLength=2).
  const country = fields.countryCode.trim().toUpperCase()
  if (!COUNTRY_CODE_RE.test(country)) {
    errors.countryCode = 'Enter a 2-letter country code (e.g. US).'
  }
  // ZIP is always required; additionally format-checked for US addresses with
  // the SAME rule the card billing ZIP uses (5-digit / ZIP+4). Left as a
  // non-blank check for other countries so a valid Canadian/JP postal code
  // isn't wrongly rejected by a US-only pattern.
  if (!fields.postalCode.trim()) {
    errors.postalCode = 'Enter a ZIP/postal code.'
  } else if (country === 'US' && !ZIP_RE.test(fields.postalCode.trim())) {
    errors.postalCode = 'Enter a valid ZIP code.'
  }
  return errors
}

export function addressFormFieldsFrom(address: AddressView): AddressFormFields {
  return {
    label: address.label,
    contactName: address.contactName ?? '',
    companyName: address.companyName ?? '',
    line1: address.line1,
    line2: address.line2 ?? '',
    city: address.city,
    region: address.region,
    postalCode: address.postalCode,
    countryCode: address.countryCode,
  }
}

function toNullableTrimmed(value: string): string | null {
  const trimmed = value.trim()
  return trimmed || null
}

/** Shared value-field shape of `CreateAddressRequest`/`UpdateAddressRequest`
 * (identical except `CreateAddressRequest` additionally allows `isDefault`,
 * which this form never sets — set-default is the separate op, ADR-0044 §2). */
function addressValueFields(fields: AddressFormFields) {
  return {
    label: fields.label.trim(),
    line1: fields.line1.trim(),
    line2: toNullableTrimmed(fields.line2),
    city: fields.city.trim(),
    region: fields.region.trim(),
    postalCode: fields.postalCode.trim(),
    countryCode: fields.countryCode.trim(),
    contactName: toNullableTrimmed(fields.contactName),
    companyName: toNullableTrimmed(fields.companyName),
  }
}

export function toCreateAddressRequest(
  fields: AddressFormFields,
  isDefault = false,
): CreateAddressRequest {
  // `isDefault` is set from the Add-address modal's "Set as default shipping
  // address" checkbox (design parity); omitted-when-false so a plain create is
  // byte-identical to before (exactOptionalPropertyTypes).
  return { ...addressValueFields(fields), ...(isDefault ? { isDefault: true } : {}) }
}

export function toUpdateAddressRequest(fields: AddressFormFields): UpdateAddressRequest {
  return addressValueFields(fields)
}

// ---------------------------------------------------------------------------
// UID-206 — saved shipping-account book (checkout §2, ADR-0067)
// ---------------------------------------------------------------------------

export type ShippingAccountView = components['schemas']['ShippingAccountView']
export type CreateShippingAccountRequest = components['schemas']['CreateShippingAccountRequest']
export type ShippingAccountMutationResult = components['schemas']['ShippingAccountMutationResult']

/** The Add-shipping-account modal's fields (prototype `shipAcctModal`, lines
 * 3667-3680): carrier / account holder / account number / billing ZIP. `region`
 * is not exposed in the modal — the create defaults it to "US" (prototype seed). */
export interface ShippingAccountFormFields {
  readonly carrier: string
  readonly holderName: string
  readonly accountNumber: string
  readonly postalCode: string
}

export const BLANK_SHIPPING_ACCOUNT_FORM: ShippingAccountFormFields = {
  carrier: '',
  holderName: '',
  accountNumber: '',
  postalCode: '',
}

/** Carrier + account number are the required fields (matches
 * `CreateShippingAccountRequest.required: [carrier, accountNumber]`); holder
 * name + ZIP are optional. Mirrors the prototype's "Name and account number are
 * required" gate, but keyed to the contract's actual required set. */
export function validateShippingAccountFields(fields: ShippingAccountFormFields): FieldErrors {
  const errors: FieldErrors = {}
  if (!fields.carrier.trim()) errors.carrier = 'Enter a carrier.'
  if (!fields.accountNumber.trim()) errors.accountNumber = 'Enter an account number.'
  return errors
}

/** Map the modal fields to `CreateShippingAccountRequest`. The FULL account
 * number is sent on create (stored server-side, never echoed back — only a
 * masked last-4 is returned). `region` defaults to "US" (prototype seed);
 * optional blanks are sent as `null`. */
export function toCreateShippingAccountRequest(
  fields: ShippingAccountFormFields,
): CreateShippingAccountRequest {
  return {
    carrier: fields.carrier.trim(),
    holderName: toNullableTrimmed(fields.holderName),
    accountNumber: fields.accountNumber.trim(),
    region: 'US',
    postalCode: toNullableTrimmed(fields.postalCode),
  }
}

/** The row's secondary line (prototype `{{ sa.carrier }} ({{ sa.acct }}) ·
 * {{ sa.region }}, {{ sa.zip }}`, e.g. "FedEx (••3433) · US, 98116"). Uses the
 * server-masked `maskedAccount` (never the full number); region/ZIP are appended
 * only when present so a partial account still reads cleanly. */
export function formatShippingAccountLine(
  account: Pick<ShippingAccountView, 'carrier' | 'maskedAccount' | 'region' | 'postalCode'>,
): string {
  const head = `${account.carrier} (${account.maskedAccount})`
  const locale = [account.region, account.postalCode].filter((part) => part?.trim()).join(', ')
  return locale ? `${head} · ${locale}` : head
}

/** The shipping account to pre-select when the own-account method is chosen:
 * the book's default, else the first account, else `null` (empty book). */
export function defaultShippingAccountId(accounts: readonly ShippingAccountView[]): string | null {
  return (accounts.find((account) => account.isDefault) ?? accounts[0])?.id ?? null
}

// ---------------------------------------------------------------------------
// UID-97 K3 — saved-payment removal (ADR-0044 §2)
// ---------------------------------------------------------------------------

export type RemovePaymentMethodResult = components['schemas']['RemovePaymentMethodResult']

// ---------------------------------------------------------------------------
// UID-97 K4 — address card contact name + company (ADR-0044 §3)
// ---------------------------------------------------------------------------

/**
 * "{{ a.name }} · {{ a.company }}" (prototype lines 1986-1987; seed lines
 * 4963-4965: "Maya Chen" / "Northwind Robotics"). `AddressView` is the
 * in-repo stand-in that carries these two fields (the frozen submodule
 * `ShipToAddress` does not — ADR-0044 §3 FLAG). Degrades gracefully when
 * either half is missing rather than rendering a bare "·", and returns
 * `null` (documented placeholder, never fabricated) when NEITHER is present.
 */
export function formatAddressContactLine(
  address: Pick<AddressView, 'contactName' | 'companyName'>,
): string | null {
  const name = address.contactName?.trim()
  const company = address.companyName?.trim()
  if (name && company) return `${name} · ${company}`
  return name || company || null
}

export type FieldErrors = Record<string, string | undefined>

/**
 * Registers a ref-callback for a named form field so `CheckoutPage` can move
 * focus to the first invalid field in document order on a failed "Place
 * order" (UX spec §3.3). Each step component calls
 * `registerFieldRef('cardNumber')` etc. as the `ref` prop of its Input.
 */
export type FieldRefRegistrar = (name: string) => (el: HTMLInputElement | null) => void

// ---------------------------------------------------------------------------
// §1 — shipping address (Stage-2a CheckoutView.shipToAddresses)
// ---------------------------------------------------------------------------

/** One-line address summary for a saved `ShipToAddress` (Stage-2a). */
export function formatShipToAddressLine(address: ShipToAddress): string {
  const line2 = address.line2 ? `, ${address.line2}` : ''
  return `${address.line1}${line2}, ${address.city}, ${address.region} ${address.postalCode}`
}

// ---------------------------------------------------------------------------
// §2 — shipping method (Stage-2a CheckoutView.shippingMethods)
// ---------------------------------------------------------------------------

/** The fastest/first `prepay` method in the rate table — the fallback row
 * selected when the buyer switches the outer choice to "Prepay shipping"
 * without having a specific row chosen yet. */
export function defaultPrepayMethodId(
  methods: readonly ShippingMethodOption[],
): string | undefined {
  return methods.find((method) => method.kind === 'prepay')?.id
}

// ---------------------------------------------------------------------------
// UID-128 (ADR-0053 §7 follow-up) — checkout shipping pre-fill FROM the
// cart's originating RFQ (`GET /checkout/shipping-seed`, frontend-local BFF
// endpoint). ALL security/tenant validation (the tenant-scoped RFQ read, the
// shipToAddressId set-membership/IDOR gate) runs server-side — this module
// only consumes the already-validated seed; it never re-derives or bypasses
// that validation.
// ---------------------------------------------------------------------------

export type CheckoutShippingSeed = components['schemas']['CheckoutShippingSeed']

/**
 * UID-617 — the DeliveryPlanStep's two stable, mode-independent shipping figures
 * (`GET /checkout/delivery-plan-rates`). See `checkout-derivation.ts`'s
 * `deriveDeliveryPlanRates` doc comment for why these must not be read off
 * `CheckoutView.summary.shippingAmount`, which is mode-DEPENDENT on purpose.
 */
export type DeliveryPlanRates = components['schemas']['DeliveryPlanRates']

/**
 * Resolve the §2 shipping-method id to seed FROM `GET /checkout/shipping-seed`, or
 * `undefined` when there is nothing valid to seed — the seed carries no
 * `shippingMethodId` (no RFQ-originated cart line, the RFQ recorded no shipping
 * selection, or the seed came back empty because a cross-tenant/unknown address failed
 * the server-side IDOR gate — ALL server-side concerns this function never re-derives),
 * or the id it names is not one of the CURRENTLY-OFFERED methods (defensive: the seed
 * and the rate table come from two separate reads that could disagree).
 *
 * PURE and READ-ONLY: this never posts anything back to the RFQ or any other route —
 * it only maps an already-fetched seed onto an already-fetched method list. The caller
 * (`CheckoutPage`) decides WHETHER/WHEN to apply the result — see its mount-effect
 * precedence comment (the customer's own explicit selection always wins over this seed).
 */
export function resolveSeededShippingMethodId(
  seed: CheckoutShippingSeed | undefined,
  methods: readonly ShippingMethodOption[],
): string | undefined {
  const id = seed?.shippingMethodId
  if (!id) return undefined
  return methods.some((method) => method.id === id) ? id : undefined
}

/**
 * Resolve the §1 ship-to-address id to seed FROM the SAME `GET /checkout/shipping-seed`
 * response, or `undefined` when there is nothing valid to seed. `seed.shipToAddressId` has
 * ALREADY passed the server-side tenant set-membership/IDOR gate (ADR-0053 §7) — a
 * cross-tenant or unknown id is dropped to `null` before it ever reaches this function, so
 * this is a defensive existence check against the CURRENTLY-OFFERED address list (the seed
 * and the address book come from two separate reads that could disagree), never a
 * re-derivation of the tenant check itself.
 *
 * PURE and READ-ONLY, mirroring {@link resolveSeededShippingMethodId} — see that function's
 * doc for the shared precedence contract (the caller decides whether/when to apply this).
 */
export function resolveSeededShipToAddressId(
  seed: CheckoutShippingSeed | undefined,
  addresses: readonly ShipToAddress[],
): string | undefined {
  const id = seed?.shipToAddressId
  if (!id) return undefined
  return addresses.some((address) => address.id === id) ? id : undefined
}

// ---------------------------------------------------------------------------
// Checkout delivery-plan step (RFQ multi-lead-time carts) — inserted between
// §1 (shipping address) and §2 (shipping method) ONLY for a cart that both
// originated from an RFQ AND resolves to more than one distinct lead time.
// This is a DELIBERATE, SCOPED exception to ADR-0106 ("checkout has no
// checkout toggle" for the cart's split-PO flag): that ADR governs the
// split-PO CHOICE specifically, not the unrelated `ShipPlan.mode` field this
// step writes through to (`PUT /cart/ship-plan`, the SAME cart-owned field
// the Cart screen's `ShipPlanCard` already drives) — no new client state, no
// new endpoint.
// ---------------------------------------------------------------------------

/** A line's first committed ship date (its lead time, at demo fidelity), or
 * `undefined` when the line carries no delivery promise yet. */
function firstCommittedDate(line: LineItem): string | undefined {
  return line.deliveryPromise?.committedDates?.[0] ?? undefined
}

/**
 * Gates the new delivery-plan step: true whenever the cart carries at least
 * one line that's FRESHLY RFQ-originated (`sourceRfqId` set, and NOT a
 * reorder) — REGARDLESS of whether the lines share one lead time or several.
 * Product directive: if it originates from an RFQ, show the step in any
 * case — the buyer's choice of "ship together" vs. "ship as ready" is a
 * meaningful decision even for a single lead time (it still governs how
 * MANY shipments a later-added or re-quoted line would split into).
 *
 * A REORDERED line (`sourceOrderId` set — `order-service.ts`'s `reorder`,
 * UID-94) carries its original order's `sourceRfqId` forward too (so
 * provenance/READY-not-pending pricing still works), but it is "buy the same
 * thing again" — already decided and priced — not a fresh RFQ→checkout
 * decision point, so it still does not count here. `sourceOrderId` lives OFF
 * the shared `LineItem` (reused by Rfq/Quote/Order — ADR-0029) on the
 * parallel `CartLinePresentation` array, joined back by `sourceableId`;
 * `presentationsBySourceableId` is optional so every OTHER caller of this
 * resolver (and every existing test) is unaffected.
 */
export function resolveShowDeliveryPlan(
  lines: readonly LineItem[],
  presentationsBySourceableId?: ReadonlyMap<string, CartLinePresentation>,
): boolean {
  return lines.some(
    (line) =>
      Boolean(line.sourceRfqId) &&
      !presentationsBySourceableId?.get(line.sourceable.id)?.sourceOrderId,
  )
}

/** Count of RFQ-originated lines — the design's "This quote has {N} parts
 * with different lead times" subheading count. */
export function deliverPartCount(lines: readonly LineItem[]): number {
  return lines.filter((line) => Boolean(line.sourceRfqId)).length
}

export interface DeliveryPlanDates {
  /** The latest committed ship date across the given lines — "ship
   * together" waits for every part, so its single shipment can't leave
   * before the slowest line is ready. `undefined` when no line carries a
   * committed date yet. */
  readonly togetherDate: string | undefined
  /** Earliest committed ship date — the first "ship as ready" shipment. */
  readonly readyFirstDate: string | undefined
  /** Latest committed ship date — the last "ship as ready" shipment (same
   * value as `togetherDate`; kept as a separate, clearly-named field so
   * callers never have to reason about why the two coincide). */
  readonly readyLastDate: string | undefined
}

/** Derives the date fields the delivery-plan cards render (`planTogDate`,
 * `planReadyFirst`/`planReadyLast` in the design). Pure and read-only. */
export function deriveDeliveryPlanDates(lines: readonly LineItem[]): DeliveryPlanDates {
  const dates = lines
    .map(firstCommittedDate)
    .filter((date): date is string => Boolean(date))
    .sort()
  if (dates.length === 0) {
    return { togetherDate: undefined, readyFirstDate: undefined, readyLastDate: undefined }
  }
  return {
    togetherDate: dates[dates.length - 1],
    readyFirstDate: dates[0],
    readyLastDate: dates[dates.length - 1],
  }
}

// ---------------------------------------------------------------------------
// §3 — payment: validation (new card / new ACH / PO fields)
// ---------------------------------------------------------------------------

const CARD_NUMBER_RE = /^\d{13,19}$/
const CVC_RE = /^\d{3,4}$/
const ZIP_RE = /^\d{5}(-\d{4})?$/
const ROUTING_RE = /^\d{9}$/
const ACCOUNT_NUMBER_RE = /^\d{4,17}$/
const EXPIRY_RE = /^(\d{2})\/(\d{2})$/
const COUNTRY_CODE_RE = /^[A-Z]{2}$/

export interface CardFormFields {
  readonly cardNumber: string
  readonly expiry: string
  readonly cvc: string
  readonly nameOnCard: string
  readonly billingZip: string
}

/** MM/YY, parseable, and not in the past relative to `now` (default: real now). */
export function isValidFutureExpiry(expiry: string, now: Date = new Date()): boolean {
  const match = EXPIRY_RE.exec(expiry.trim())
  if (!match?.[1] || !match[2]) return false
  const month = Number(match[1])
  const year = 2000 + Number(match[2])
  if (month < 1 || month > 12) return false
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  if (year < currentYear) return false
  if (year === currentYear && month < currentMonth) return false
  return true
}

/**
 * The card-expiry field error, or `undefined` when the expiry is a valid
 * future date. Distinguishes a MALFORMED/incomplete value (`""`, `"13/26"`,
 * `"nope"` — the buyer is still typing or fat-fingered it) from a well-formed
 * but PAST date. The old code reported "This card has expired." for both,
 * which was misleading for anything that simply wasn't a parseable MM/YY yet.
 */
export function cardExpiryError(expiry: string, now?: Date): string | undefined {
  const match = EXPIRY_RE.exec(expiry.trim())
  const month = match?.[1] ? Number(match[1]) : Number.NaN
  if (!match?.[1] || !match[2] || month < 1 || month > 12) {
    return 'Enter a valid expiry date (MM/YY).'
  }
  return isValidFutureExpiry(expiry, now) ? undefined : 'This card has expired.'
}

export function validateCardFields(fields: CardFormFields, now?: Date): FieldErrors {
  const errors: FieldErrors = {}
  const digitsOnly = fields.cardNumber.replace(/[\s-]/g, '')
  if (!CARD_NUMBER_RE.test(digitsOnly)) errors.cardNumber = 'Enter a valid card number.'
  const expiryError = cardExpiryError(fields.expiry, now)
  if (expiryError) errors.expiry = expiryError
  if (!CVC_RE.test(fields.cvc.trim())) errors.cvc = 'Enter a valid security code.'
  if (!fields.nameOnCard.trim()) errors.nameOnCard = 'Enter the name on the card.'
  if (!ZIP_RE.test(fields.billingZip.trim())) errors.billingZip = 'Enter a valid ZIP code.'
  return errors
}

export type AchAccountType = 'checking' | 'savings' | ''

export interface AchFormFields {
  readonly routingNumber: string
  readonly accountNumber: string
  readonly accountType: AchAccountType
}

export function validateAchFields(fields: AchFormFields): FieldErrors {
  const errors: FieldErrors = {}
  if (!ROUTING_RE.test(fields.routingNumber.trim())) {
    errors.routingNumber = 'Enter a valid 9-digit routing number.'
  }
  if (!ACCOUNT_NUMBER_RE.test(fields.accountNumber.trim())) {
    errors.accountNumber = 'Enter a valid account number.'
  }
  if (fields.accountType !== 'checking' && fields.accountType !== 'savings') {
    errors.accountType = 'Select Checking or Savings.'
  }
  return errors
}

export function validatePoFields(poNumber: string): FieldErrors {
  const errors: FieldErrors = {}
  if (!poNumber.trim()) errors.poNumber = 'PO number is required.'
  return errors
}

// ---------------------------------------------------------------------------
// §3 — SIMULATED tokenization (the security boundary, UX spec §2.3.4 /
// ADR-0038 §5). See docs/design/uid-60-checkout-ux-spec.md §2.3.4 and the
// module doc comment on CheckoutPage.tsx for the full non-negotiable list.
// ---------------------------------------------------------------------------

/**
 * Produces an opaque, non-reversible-looking demo token from RANDOMNESS
 * ONLY — the raw card/account number never participates in deriving this
 * string (not base64 of the PAN, not a hash of it; a hash would still be a
 * reversible-in-spirit "transform of the raw input" the spec explicitly
 * forbids as a token-derivation strategy, and would risk callers assuming
 * they could recover anything from it). The caller passes this — and NEVER
 * the raw value — to `POST /api/orders`; the raw value is discarded the
 * moment this returns.
 */
export function generateSimulatedTokenRef(): string {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : fallbackId()
  return `sim_tok_${id}`
}

function fallbackId(): string {
  return Array.from({ length: 4 }, () => Math.random().toString(36).slice(2)).join('')
}

/**
 * Display-only "last 4" label (e.g. "•••• 4242") — the UI never re-renders
 * a full card/account number or CVC anywhere after entry, including this
 * label (UX spec §2.3.4's "design-level tell"). Never used for authorization.
 */
export function maskedDisplayLabel(rawDigits: string): string {
  const digitsOnly = rawDigits.replace(/\D/g, '')
  return `•••• ${digitsOnly.slice(-4)}`
}

// ---------------------------------------------------------------------------
// §3.5 — server-side rejection after client-side validation passed
// ---------------------------------------------------------------------------

export type CheckoutPlacementErrorKind =
  | 'payment_method_not_eligible'
  | 'terms_not_accepted'
  | 'no_ready_lines'
  | 'invalid_selection'
  | 'invalid_grouping'
  | 'generic'

/**
 * Classifies a `POST /api/orders`(`/batch`) 4xx `Problem.type`
 * (`apps/api/src/modules/order/routes.ts` `orderErrorToProblem` emits
 * `order/terms_not_accepted` / `order/payment_method_not_eligible` /
 * `order/no_ready_lines` / `order/invalid_selection` / — UID-97 K1 —
 * `order/invalid_grouping`) into the banner variant §3.5 specifies. Falls
 * back to 'generic' (toast, not a banner) for anything unrecognized,
 * INCLUDING no `type` at all — never throws on a shape it doesn't expect.
 */
export function classifyCheckoutPlacementError(
  problemType: string | undefined,
): CheckoutPlacementErrorKind {
  if (!problemType) return 'generic'
  if (problemType.includes('payment_method_not_eligible')) return 'payment_method_not_eligible'
  if (problemType.includes('terms_not_accepted')) return 'terms_not_accepted'
  if (problemType.includes('no_ready_lines')) return 'no_ready_lines'
  if (problemType.includes('invalid_grouping')) return 'invalid_grouping'
  if (problemType.includes('invalid_selection')) return 'invalid_selection'
  return 'generic'
}
